import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FiX, FiPlus, FiEdit2, FiBold, FiItalic, FiList } from 'react-icons/fi';

export default function BlogFormModal({
  isOpen,
  onClose,
  editingBlog,
  API_URL,
  token,
  triggerNotification,
  fetchData
}) {
  const [blogForm, setBlogForm] = useState({
    title: '',
    summary: '',
    content: '',
    category: 'Farming Tips',
    image_url: ''
  });

  const editorRef = useRef(null);

  // Initialize and sync editor content when modal opens or editing changes
  useEffect(() => {
    if (isOpen) {
      const initialContent = editingBlog ? (editingBlog.content || '') : '';
      if (editorRef.current) {
        editorRef.current.innerHTML = initialContent;
      }
    }
  }, [isOpen, editingBlog]);

  const handleEditorChange = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      setBlogForm(prev => ({ ...prev, content: html }));
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (editingBlog) {
        setBlogForm({
          title: editingBlog.title || '',
          summary: editingBlog.summary || '',
          content: editingBlog.content || '',
          category: editingBlog.category || 'Farming Tips',
          image_url: editingBlog.image_url || ''
        });
      } else {
        setBlogForm({
          title: '',
          summary: '',
          content: '',
          category: 'Farming Tips',
          image_url: ''
        });
      }
    }
  }, [isOpen, editingBlog]);

  if (!isOpen) return null;

  const handleBlogImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Illustration image is too large. Maximum size is 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setBlogForm(prev => ({ ...prev, image_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    
    // Clean up empty tag placeholders and validate content
    const plainText = editorRef.current ? editorRef.current.innerText.trim() : '';
    if (!blogForm.content || blogForm.content === '<br>' || blogForm.content === '<div><br></div>' || plainText === '') {
      triggerNotification('Please write some content for the article.');
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    try {
      if (editingBlog) {
        await axios.put(`${API_URL}/blogs/${editingBlog.id}`, blogForm, { headers });
      } else {
        await axios.post(`${API_URL}/blogs`, blogForm, { headers });
      }

      triggerNotification(editingBlog ? 'Blog article modified.' : 'New blog article published.');
      onClose();
      fetchData();
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Failed to submit blog post.';
      triggerNotification(errorMessage);
    }
  };

  return (
    <div className="admin-modal-overlay-bg" onClick={onClose}>
      <div className="admin-modal-form-card animate-scale-up" onClick={(e) => e.stopPropagation()}>
        <header className="modal-form-header">
          <h2>{editingBlog ? <><FiEdit2 className="modal-header-icon" /> Edit Article</> : <><FiPlus className="modal-header-icon" /> Write New Article</>}</h2>
          <button className="modal-close-x-btn" onClick={onClose} aria-label="Close modal"><FiX /></button>
        </header>
        <form onSubmit={handleBlogSubmit} className="dashboard-grid-form">
          <div className="form-group flex-2">
            <label>Article Title *</label>
            <input
              type="text"
              value={blogForm.title}
              onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
              placeholder="e.g. Best Practices for Vermicomposting in 2026"
              required
            />
          </div>

          <div className="form-group">
            <label>Knowledge Category *</label>
            <select
              value={blogForm.category}
              onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })}
            >
              <option value="Farming Tips">Farming Tips</option>
              <option value="Bio Pest">Bio Pest</option>
              <option value="Organic">Organic</option>
              <option value="Soil Health">Soil Health</option>
            </select>
          </div>

          <div className="form-group flex-3">
            <label>Illustration Image (Max 5MB)</label>
            <div className="gallery-upload-container">
              <input
                type="file"
                accept="image/*"
                onChange={handleBlogImageUpload}
                className="gallery-file-input"
                id="blog-image-input"
              />
              <label htmlFor="blog-image-input" className="gallery-upload-trigger">
                <FiPlus className="upload-plus-icon" /> Choose Image File
              </label>
            </div>
            {blogForm.image_url && (
              <div className="brochure-preview-wrap" style={{ marginTop: '12px' }}>
                <img
                  src={blogForm.image_url}
                  alt="Blog Preview"
                  className="brochure-preview-img"
                />
                <button
                  type="button"
                  className="gallery-remove-btn brochure-remove-btn"
                  onClick={() => setBlogForm(prev => ({ ...prev, image_url: '' }))}
                  title="Remove Image"
                >
                  <FiX /> Remove
                </button>
              </div>
            )}
          </div>

          <div className="form-group flex-3">
            <label>Article Brief Summary *</label>
            <input
              type="text"
              value={blogForm.summary}
              onChange={(e) => setBlogForm({ ...blogForm, summary: e.target.value })}
              placeholder="Brief summary showing up in the blogs overview list..."
              required
            />
          </div>

          <div className="form-group flex-3">
            <label>Complete Article Content *</label>
            <div className="custom-rich-editor">
              <div className="rich-editor-toolbar">
                <button
                  type="button"
                  className="rich-tool-btn"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    document.execCommand('bold', false);
                    handleEditorChange();
                  }}
                  title="Bold"
                >
                  <FiBold />
                </button>
                <button
                  type="button"
                  className="rich-tool-btn"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    document.execCommand('italic', false);
                    handleEditorChange();
                  }}
                  title="Italic"
                >
                  <FiItalic />
                </button>
                <button
                  type="button"
                  className="rich-tool-btn"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    document.execCommand('insertOrderedList', false);
                    handleEditorChange();
                  }}
                  title="Numbered List"
                >
                  <FiList />
                </button>

                <div style={{ width: '1px', height: '20px', backgroundColor: '#e2e8f0', margin: '0 4px' }} />

                <select
                  className="rich-tool-select"
                  onChange={(e) => {
                    const color = e.target.value;
                    if (color) {
                      document.execCommand('foreColor', false, color);
                      handleEditorChange();
                    }
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>Color</option>
                  <option value="#334155">Default</option>
                  <option value="#1c6901">Green (Brand)</option>
                  <option value="#363b6d">Blue (Brand)</option>
                  <option value="#f1921d">Orange (Brand)</option>
                  <option value="#dc2626">Red</option>
                </select>

                <select
                  className="rich-tool-select"
                  onChange={(e) => {
                    const font = e.target.value;
                    if (font) {
                      document.execCommand('fontName', false, font);
                      handleEditorChange();
                    }
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>Font Family</option>
                  <option value="Inter, sans-serif">Inter</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="Courier New, monospace">Monospace</option>
                  <option value="'Outfit', sans-serif">Outfit</option>
                </select>
              </div>

              <div
                ref={editorRef}
                className="rich-editor-content"
                contentEditable
                onInput={handleEditorChange}
                onBlur={handleEditorChange}
                placeholder="Write full article here..."
              />
            </div>
          </div>

          <footer className="form-action-footer flex-3">
            <button type="button" className="btn-secondary-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary-submit">
              {editingBlog ? 'Save Changes' : 'Publish Blog Article'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
